import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { AI_SMART_COMPARE_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import {
    MultiSecurityQuickflowCollectionResponse,
    QuickflowSecurityInfo,
} from 'QuickflowsApp/ts/interfaces/quickflow-view-responses.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import { QuickflowsService } from 'QuickflowsApp/ts/services/quickflows.service';

import { IconSearchAutocompleteComponent } from 'SearchApp/ts/features/icon-search-autocomplete/icon-search-autocomplete.component';

/**
 *
 * Multi Select Quickflows Component
 *
 * This component is one tab of the quickflows menu.
 *
 * Input Properties:
 *   displaySecurityId: The display security ID to initialize this component with, e.g. VTSAX
 *   securityId: The security ID to initialize this component with, e.g. M:VTSAX
 *
 */

@Component({
    standalone: false,
    selector: 'ycn-multi-select-quickflows',
    templateUrl: './multi-select-quickflows.template.html',
})
export class MultiSelectQuickflowsComponent implements OnInit {
    @Input() displaySecurityId?: string;

    @Input() securityId?: string;

    @Input() securityName?: string;

    @Input() securityUrl?: string;

    @ViewChild(IconSearchAutocompleteComponent) autocompleteComponent!: IconSearchAutocompleteComponent;

    availableQuickflows: MultiSecurityQuickflowCollectionResponse[] = [];

    hasSmartCompareFeature: boolean = false;

    loadingCollection: boolean = false;

    items: QuickflowSecurityInfo[] = [];

    constructor(private quickflowsService: QuickflowsService, private featureService: FeatureService) {}

    ngOnInit(): void {
        this.hasSmartCompareFeature = this.featureService.userHasFeatures([AI_SMART_COMPARE_FEATURE]);
        this.quickflowsService.getMultiSecurityQuickflowsList().subscribe((data) => {
            const { securityIds } = data;
            if (securityIds && securityIds.length > 0) {
                this.items = securityIds;
                this.refreshQuickflowCollection();
            } else if (this.securityId && this.displaySecurityId && this.securityName && this.securityUrl) {
                this.addItem(
                    {
                        display_security_id: this.displaySecurityId,
                        search_id: this.securityId,
                        name: this.securityName,
                        url: this.securityUrl,
                    },
                    false
                );
            }
        });
    }

    select(quickflowId: string): void {
        this.quickflowRedirect(quickflowId);
    }

    /**
     * Adds a security to the list of comparison quickflows securities.
     * @param securityObject security to add
     * @param save whether to save the updated list of securities to the database.
     * This will be false if we are adding the current page security and not one chosen by the user, because
     * we want to add the current page security any time they switch to a different quote page unless they
     * have added different securities from the autocompleter.
     */
    addItem(
        securityObject: Pick<AutocompleteResult, 'display_security_id' | 'search_id' | 'name' | 'url'>,
        save: boolean = true
    ): void {
        const MAX_ITEMS = 12;

        // do nothing if the securityId is already part of the items list
        for (let i = 0; i < this.items.length; i += 1) {
            if (this.items[i].securityId === securityObject.search_id) return;
        }

        if (this.items.length === MAX_ITEMS) return;

        // autocompleter uses `search_id` which is essentially a security's `securityId`
        this.items.push({
            displaySecurityId: securityObject.display_security_id,
            securityId: securityObject.search_id,
            securityName: securityObject.name,
            securityUrl: securityObject.url,
        });

        // snapshot the current list of securities & update
        if (save) {
            this.updateQuickflowsList();
        }
        this.refreshQuickflowCollection();
    }

    deleteItem(index: number): void {
        this.items.splice(index, 1);

        // snapshot the current list of securities & update
        this.updateQuickflowsList();
        this.refreshQuickflowCollection();
    }

    quickflowRedirect(quickflowId: string): void {
        const securityIds = this.getSecurityIds();
        this.quickflowsService.getMultiSecurityQuickflowsRedirect(quickflowId, securityIds).subscribe((data) => {
            const quickflowURL = new URL(data.url);
            // Change the browser URL to the quickflows URL.
            window.location.assign(quickflowURL.href);
            // If the path of the application is not changing we need to set the
            // url state and reload so that the application loads from scratch.
            if (quickflowURL.pathname === window.location.pathname) {
                window.location.reload();
            }
        });
    }

    updateQuickflowsList(): void {
        const securityIds = this.getSecurityIds();
        this.quickflowsService.putMultiSecurityQuickflowsList(securityIds).subscribe({
            next: () => {},
            error: (error: HttpErrorResponse) => {
                // If user is logged out, PUT will fail with 403 or 405. Re-raise any other errors
                if (error.status !== 403 && error.status !== 405) {
                    throw error;
                }
            },
        });
    }

    refreshQuickflowCollection(): void {
        // only update the quickflow collection if there are at least 2 securities selected
        if (this.items.length > 1) {
            this.updateQuickflowCollection();
        }

        this.focusAutocompleter();
    }

    updateQuickflowCollection(): void {
        this.loadingCollection = true;

        const securityIds = this.getSecurityIds();
        this.quickflowsService
            .getMultiSecurityQuickflowsCollection(securityIds)
            .pipe(
                finalize(() => {
                    this.loadingCollection = false;
                })
            )
            .subscribe({
                next: (data) => {
                    this.availableQuickflows = data.filter((quickflowsByApp) => quickflowsByApp.quickflows.length > 0);
                },
                error: () => {
                    this.availableQuickflows = [];
                },
            });
    }

    getSecurityIds(): string {
        const securityIds = this.items.map(({ securityId }) => securityId);
        return securityIds.join(',');
    }

    public focusAutocompleter(): void {
        if (this.autocompleteComponent) {
            this.autocompleteComponent.focus();
        }
    }

    handleSmartCompareClick(): void {
        const smartCompareIds = this.items.map((item) => item.securityId);
        const prompt = this.buildSmartComparePrompt(smartCompareIds);
        const queryParams = new URLSearchParams({
            smartComparePrompt: prompt,
            smartCompareIds: smartCompareIds.join(','),
        });

        window.location.href = `/agent/#/?${queryParams.toString()}`;
    }

    private buildSmartComparePrompt(ids: string[]): string {
        if (ids.length === 1) return `Smart Compare ${ids[0]}`;
        if (ids.length === 2) return `Smart Compare ${ids[0]} and ${ids[1]}`;
        return `Smart Compare ${ids.slice(0, -1).join(', ')} and ${ids[ids.length - 1]}`;
    }
}
